/**
 * Purchase Orders API
 *
 * API client for purchase order operations
 */

import type {
  PurchaseOrder,
  CreatePurchaseOrderDto,
  UpdatePurchaseOrderDto,
  ReceivePurchaseOrderDto,
  PurchaseOrderKpis,
  PurchaseOrderStatus,
  OrderLineItem,
  Priority,
} from '@lognai/plugin-orders';
import type { PaginationMetadata } from '@lognai/plugin-sdk';
import { apiFetch, buildQueryString, type ListQueryParams } from './client';

const BASE_URL = '/api/orders/purchase-orders';

/**
 * Purchase order domain filter fields
 */
export interface PurchaseOrderFilter {
  /** Free text over the order number and the counterparty (QA ledger ORD-006). */
  search?: string;
  status?: PurchaseOrderStatus;
  supplierId?: string;
  warehouseId?: string;
  priority?: Priority;
  fromDate?: Date;
  toDate?: Date;
  expectedDeliveryDate?: { from: string; to: string };
  /**
   * Access tags, `key:value`: any value within a tag, every tag across. Sent as
   * repeated top-level `tag=` params, not through the shared builder.
   */
  tag?: string[];
  /** Company custom fields marked filterable: `{ division: 'North', 'consignee.name': 'ACME' }`. */
  cf?: Record<string, string>;
}

/**
 * Purchase order list query parameters (pagination + filters)
 */
export type PurchaseOrderFilterParams = ListQueryParams<PurchaseOrderFilter>;

/**
 * Paginated purchase order list response
 */
export interface PaginatedPurchaseOrderList {
  items: PurchaseOrder[];
  pagination: PaginationMetadata | null;
}

/**
 * The list query string. Access tags are written as repeated top-level params
 * (`tag=businessUnit:A&tag=businessUnit:B`); the shared builder would nest and
 * comma-join them.
 */
export const buildPurchaseOrderQuery = (params?: PurchaseOrderFilterParams): string => {
  const { tag, ...filter } = params?.filter ?? {};
  const base = buildQueryString(params ? { ...params, filter } : params);
  const extra = new URLSearchParams();
  for (const value of tag ?? []) {
    if (value !== '') extra.append('tag', value);
  }
  const suffix = extra.toString();
  if (!suffix) return base;
  return base ? `${base}&${suffix}` : `?${suffix}`;
};

/**
 * Get all purchase orders with optional filters
 */
export const listPurchaseOrders = async (
  params?: PurchaseOrderFilterParams
): Promise<PaginatedPurchaseOrderList> => {
  return apiFetch<PaginatedPurchaseOrderList>(`${BASE_URL}${buildPurchaseOrderQuery(params)}`);
};

/**
 * Get a single purchase order by ID
 */
export const getPurchaseOrder = async (id: string): Promise<PurchaseOrder> => {
  return apiFetch<PurchaseOrder>(`${BASE_URL}/${id}`);
};

/**
 * Create a new purchase order
 */
export const createPurchaseOrder = async (
  dto: CreatePurchaseOrderDto
): Promise<PurchaseOrder> => {
  return apiFetch<PurchaseOrder>(BASE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dto),
  });
};

/**
 * Update a purchase order
 */
export const updatePurchaseOrder = async (
  id: string,
  dto: UpdatePurchaseOrderDto
): Promise<PurchaseOrder> => {
  return apiFetch<PurchaseOrder>(`${BASE_URL}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dto),
  });
};

/**
 * Update purchase order status
 */
export const updatePurchaseOrderStatus = async (
  id: string,
  status: PurchaseOrderStatus,
  reason?: string
): Promise<PurchaseOrder> => {
  return apiFetch<PurchaseOrder>(`${BASE_URL}/${id}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, reason }),
  });
};

/**
 * Cancel a purchase order
 */
export const cancelPurchaseOrder = async (
  id: string,
  reason?: string
): Promise<PurchaseOrder> => {
  return apiFetch<PurchaseOrder>(`${BASE_URL}/${id}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
};

/**
 * Receive goods for a purchase order
 */
export const receivePurchaseOrder = async (
  id: string,
  dto: ReceivePurchaseOrderDto
): Promise<PurchaseOrder> => {
  return apiFetch<PurchaseOrder>(`${BASE_URL}/${id}/receive`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dto),
  });
};

/**
 * Get purchase order line items
 */
export const getPurchaseOrderLines = async (
  id: string
): Promise<OrderLineItem[]> => {
  return apiFetch<OrderLineItem[]>(`${BASE_URL}/${id}/lines`);
};

/**
 * Get purchase order KPIs
 */
export const getPurchaseOrderKpis = async (): Promise<PurchaseOrderKpis> => {
  return apiFetch<PurchaseOrderKpis>(`${BASE_URL}/kpis`);
};
